import { useMemo } from 'react';
import { useOpeningHours } from '@/features/opening-hours/hooks/useOpeningHours';
import { useDeliveryReadiness } from '@/features/order-setup/hooks/useOrderSetup';
import { useMenuHealth } from '@/features/menu/hooks/useMenuHealth';

// A setup gap the owner must close before the storefront works correctly. Each
// one is a recurring "the operator forgot to configure X" that silently breaks
// ordering (e.g. no delivery zone → every address reads "outside delivery area").
export type SettingsHealthCode =
  | 'location'
  | 'openingHours'
  | 'deliveryPricing'
  | 'deliveryTime'
  | 'paymentMethod'
  | 'menu'
  | 'menuNotLive';

export interface SettingsHealthIssue {
  code: SettingsHealthCode;
  // Settings sub-page (or top-level section) that fixes this gap.
  route: string;
  // Optional parent page to open first, so the fix page's Back button returns
  // there instead of dead-ending (Location is a child of the business profile).
  parentRoute?: string;
  // The route is a top-level section (e.g. Menu), not a Settings sub-page — the
  // banner navigates the main view instead of the settings-detail router.
  mainDestination?: boolean;
}

/**
 * Derives the list of unfinished-setup problems for the active restaurant from
 * the config the dashboard already loads — no new endpoint. The authoritative
 * data stays server-side; this only presents it. Checks are evaluated per query
 * so a still-loading (or errored) query never raises a false alarm.
 *
 * The location + delivery-pricing checks only fire when DELIVERY is enabled —
 * a pickup/reservation-only restaurant is never nagged about delivery it doesn't
 * use. The menu check fires when online ordering (pickup/delivery) is on. Opening
 * hours is universal (no hours → the storefront reads as always closed for every
 * channel).
 */
export function useSettingsHealth(): { issues: SettingsHealthIssue[] } {
  const { data: hours } = useOpeningHours();
  // Delivery problems come from the server's readiness check — the same rule the
  // Order Setup screen shows and, more importantly, evaluated against the same
  // config the storefront quote path reads. Deriving them here a second time is
  // how the two views drift apart (the old client checks flagged an unpinned
  // location even in map-area mode, where coordinates are never consulted).
  const { data: readiness } = useDeliveryReadiness();
  const menuHealth = useMenuHealth();

  const issues = useMemo<SettingsHealthIssue[]>(() => {
    const out: SettingsHealthIssue[] = [];
    // 1. Delivery setup problems, straight from the server's readiness check.
    //    Several zones can be missing a delivery time; the banner is one row per
    //    KIND of problem, so collapse duplicates.
    for (const code of new Set((readiness?.problems ?? []).map((p) => p.code))) {
      switch (code) {
        case 'LOCATION_NOT_PINNED':
          out.push({
            code: 'location',
            route: '/settings/restaurant/location',
            parentRoute: '/settings/restaurant',
          });
          break;
        case 'NO_ACTIVE_ZONES':
          out.push({ code: 'deliveryPricing', route: '/settings/orders' });
          break;
        case 'ZONE_MISSING_DELIVERY_TIME':
          out.push({ code: 'deliveryTime', route: '/settings/orders' });
          break;
        case 'NO_PAYMENT_METHOD':
          out.push({ code: 'paymentMethod', route: '/settings/payments' });
          break;
      }
    }

    // 2. No opening hours — needs at least one enabled BASE day with a shift,
    //    otherwise the restaurant is closed on every channel, always.
    if (hours && !hours.some((h) => h.service === 'BASE' && h.isEnabled && h.shifts.length > 0)) {
      out.push({ code: 'openingHours', route: '/settings/opening-hours' });
    }

    if (menuHealth) out.push({ code: menuHealth, route: '/menu', mainDestination: true });

    return out;
  }, [hours, readiness, menuHealth]);

  return { issues };
}
